const { getElement } = window.BAO.Utils;
class LazyVideo extends window.BAO.CustomElement() {
  static get styles() {
    return `
      :host {
        display: contents;

        opacity: 0;

        transition: opacity 0.5s ease;
      }
      :host([loaded]) {
        opacity: 1;
      }
    `;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.video) return;

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    });
    // We need to observe the video element as the host element has display: contents on it. IntersectionObserver won't work on an element that is set to display: contents
    this.observer.observe(this.video);
  }

  setupListeners() {
    super.setupListeners();

    if (!this.video) return;

    if (this.video.hasAttribute("autoplay")) {
      this.listeners.add(this.video, "playing", () =>
        this.toggleAttribute("loaded", true),
      );
    } else {
      this.listeners.add(this.video, "canplay", () =>
        this.toggleAttribute("loaded", true),
      );
    }
  }

  onEntry([entry], observer) {
    if (!entry.isIntersecting) return;

    this.loadVideo(observer);
  }

  loadVideo(observer) {
    if (this.video.hasAttribute("data-poster")) {
      this.video.setAttribute("poster", this.video.dataset.poster);
    }

    window.BAO.Utils.getElements("source[data-src]", {
      context: this.video,
    })?.forEach((sourceEl) => {
      sourceEl.src = sourceEl.dataset.src;
      this.video.load();
    });

    observer.unobserve(this.video);

    if (
      window.BAO.Utils.viewportSize() !== "xs" ||
      this.video.hasAttribute("autoplay") === false
    ) {
      return;
    }

    if (this.video.paused) {
      document.addEventListener("touchstart", () => {
        if (this.video.paused) {
          this.video.play();
        }
      });
    }
  }

  get video() {
    return getElement("video", { context: this });
  }
}

if (!customElements.get("lazy-video")) {
  customElements.define("lazy-video", LazyVideo);
}

class LazyVideoContainer extends window.BAO.CustomElement() {
  connectedCallback() {
    super.connectedCallback();
  }

  setupListeners() {
    super.setupListeners();

    if (this.video) {
      this.listeners.add(this.video, "playing", () =>
        this.toggleAttribute("playing", true),
      );

      this.listeners.add(this.video, "pause", () =>
        this.toggleAttribute("playing", false),
      );
    }

    this.listeners.add(this, "click", this.handleClick.bind(this));
  }

  handleClick(event) {
    this.handlePlay(event);
    this.handleMute(event);
  }

  handlePlay(event) {
    const buttonEl = event.target.closest(this.els.play?.selector);
    if (!buttonEl) return;

    if (this.video) {
      if (this.video.paused) {
        buttonEl.setAttribute("aria-label", buttonEl.dataset?.pauseTranslation);
        this.video.play();

        if (!this.hasBeenMuted) {
          this.video.muted = false;
        }
      } else {
        buttonEl.setAttribute("aria-label", buttonEl.dataset?.playTranslation);
        this.video.pause();
      }
    } else {
      this.toggleAttribute("novideo", true);
    }
  }

  handleMute(event) {
    const buttonEl = event.target.closest(this.els.mute?.selector);
    if (!buttonEl) return;

    if (this.video) {
      if (this.video.muted) {
        buttonEl.setAttribute("aria-label", buttonEl.dataset?.muteTranslation);
        this.video.muted = false;
      } else {
        buttonEl.setAttribute(
          "aria-label",
          buttonEl.dataset?.unmuteTranslation,
        );
        this.video.muted = true;
        this.hasBeenMuted = true;
      }
    } else {
      this.toggleAttribute("novideo", true);
    }
  }

  get video() {
    const videoEl = getElement("lazy-video", { context: this })?.video;

    if (!videoEl) {
      this.toggleAttribute("novideo", true);
    }

    return videoEl;
  }
}

if (!customElements.get("lazy-video-container")) {
  customElements.define("lazy-video-container", LazyVideoContainer);
}
